/** Promo / coupon form options — mirrors PromoCode-RegBranch schema enums. */

export const PROMO_TYPE_OPTIONS = [
  { value: 'free_order', label: 'Free order' },
  { value: 'percentage_off', label: 'Percentage off' },
  { value: 'free_delivery', label: 'Free delivery' },
  { value: 'amount_off', label: 'Amount off' },
  { value: 'free_product', label: 'Free product' },
  { value: 'free_combo', label: 'Free combo' },
  { value: '5th_order_amount_off', label: '5th order · amount off' },
  { value: '2nd_order_amount_off', label: '2nd order · amount off' },
  { value: 'happy_hour_amount_off', label: 'Happy hour · amount off' },
  { value: 'happy_hour_percentage_off', label: 'Happy hour · percentage off' },
];

export const PROMO_CONDITION_OPTIONS = [
  { value: 'new-to-store', label: 'New-to-store' },
  { value: 'old-to-store', label: 'Old-to-store' },
  { value: 'new-customer', label: 'New customer (platform)' },
  { value: 'old', label: 'Returning customer (platform)' },
];

export const PROMO_APPLICABLE_TO_OPTIONS = [
  { value: 'payAsYouGoOrder', label: 'Pay as you go' },
  { value: 'savePassUsers', label: 'SavePass users' },
  { value: 'dedicatedCustomer', label: 'Dedicated customer' },
];

export const PROMO_MENU_OPTIONS = [
  { value: 'none', label: 'Any menu' },
  { value: 'regular_menu', label: 'Regular menu' },
  { value: 'combo_menu', label: 'Combo menu' },
];

export const isPercentagePromoType = (type) =>
  type === 'percentage_off' || type === 'happy_hour_percentage_off';

export const isHappyHourPromoType = (type) =>
  type === 'happy_hour_amount_off' || type === 'happy_hour_percentage_off';

export const isFreeProductPromoType = (type) =>
  type === 'free_product' || type === 'free_combo';

export const isAmountPromoType = (type) =>
  type === 'amount_off' ||
  type === '5th_order_amount_off' ||
  type === '2nd_order_amount_off' ||
  type === 'happy_hour_amount_off';

export const getEmptyCouponForm = () => ({
  code: '',
  type: 'amount_off',
  promo_label: '',
  percentage_off_cap: '',
  applicable_menu: 'none',
  happy_hour_start_time: '',
  happy_hour_end_time: '',
  value: '',
  minimumOrderValue: '',
  applicableTo: 'payAsYouGoOrder',
  dedicatedCustomerEmail: '',
  conditionType: 'new-to-store',
  startDate: '',
  endDate: '',
  product_name: '',
  promo_status: true,
  product_image_advertisement_url: '',
});

/**
 * Builds API-shaped payload from the create-coupon form.
 * System fields (createdBy, promoCodeOwner, etc.) are filled by the caller.
 */
export const buildCouponPayloadFromForm = (form) => {
  const emails = String(form.dedicatedCustomerEmail ?? '')
    .split(/[,;\n]+/)
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean);

  const cap = String(form.percentage_off_cap ?? '').trim();
  const label = String(form.promo_label ?? '').trim();
  const productName = String(form.product_name ?? '').trim();
  const imageUrl = String(form.product_image_advertisement_url ?? '').trim();
  const hhStart = String(form.happy_hour_start_time ?? '').trim();
  const hhEnd = String(form.happy_hour_end_time ?? '').trim();
  const endDate = String(form.endDate ?? '').trim();

  return {
    code: String(form.code ?? '')
      .trim()
      .toLowerCase()
      .slice(0, 15),
    type: form.type,
    promo_label: label ? label.toLowerCase() : 'none',
    percentage_off_cap: cap ? String(cap).toLowerCase() : 'none',
    applicable_menu: form.applicable_menu || 'none',
    happy_hour_start_time: hhStart ? hhStart.toLowerCase() : 'none',
    happy_hour_end_time: hhEnd ? hhEnd.toLowerCase() : 'none',
    value: Number(form.value) || 0,
    minimumOrderValue: Number(form.minimumOrderValue) || 0,
    applicableTo: form.applicableTo || 'payAsYouGoOrder',
    dedicatedCustomerEmail: form.applicableTo === 'dedicatedCustomer' ? emails : [],
    conditionType: form.conditionType || 'old',
    startDate: String(form.startDate ?? '')
      .trim()
      .toLowerCase(),
    endDate: endDate ? endDate.toLowerCase() : null,
    product_name: productName ? productName.toLowerCase() : null,
    promo_status: form.promo_status !== false,
    product_image_advertisement_url: imageUrl ? imageUrl.toLowerCase() : null,
    promoCodeOwner: 'kitchen',
  };
};

/** Card preview helpers for the coupons list UI. */
export const promoTypeLabel = (type) =>
  PROMO_TYPE_OPTIONS.find((o) => o.value === type)?.label ?? type;

export const formatCouponCardTitle = (payload) => {
  const label = payload.promo_label && payload.promo_label !== 'none' ? payload.promo_label : '';
  if (label) {
    return label.replace(/\b\w/g, (c) => c.toUpperCase());
  }
  switch (payload.type) {
    case 'free_delivery':
      return '$0 Delivery';
    case 'free_order':
      return 'Free Order';
    case 'percentage_off':
    case 'happy_hour_percentage_off':
      return `${payload.value}% off`;
    case 'amount_off':
    case '5th_order_amount_off':
    case '2nd_order_amount_off':
    case 'happy_hour_amount_off':
      return `$${payload.value} off`;
    case 'free_product':
      return 'Free Product';
    case 'free_combo':
      return 'Free Combo';
    default:
      return promoTypeLabel(payload.type);
  }
};

export const formatCouponCardSubtitle = (payload) => {
  const min = Number(payload.minimumOrderValue) || 0;
  if (min > 0) return `when you spend $${min}+`;
  if (payload.type === 'free_product' && payload.product_name) {
    return String(payload.product_name);
  }
  return promoTypeLabel(payload.type);
};

export const formatCouponCardFooter = (payload) => {
  if (payload.endDate) {
    return `Ends ${payload.endDate}`;
  }
  return `Code ${String(payload.code || '').toUpperCase()}`;
};

export const couponListCategoryFromPayload = (payload) => {
  if (payload.applicableTo === 'dedicatedCustomer') return 'dedicated-customer';
  if (payload.conditionType === 'new-to-store') return 'new-to-store';
  if (payload.conditionType === 'old-to-store') return 'old-to-store';
  return 'all';
};
